import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { ChevronLeft, Cake, Heart, Bell, Gift, MessageCircle } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, EmptyState, HeroCard, LoadError, Pill, SectionHeader } from "@/components/dryvn";
import { CollapsibleList, ListSearch, useListFilter } from "@/components/collapsible-list";
// Screen migration (2026-07-05): dates via hook.
import { type ImportantDate } from "@/data/mock";
import { useImportantDates } from "@/lib/queries";

export const Route = createFileRoute("/more/dates")({
  head: () => ({
    meta: [
      { title: "Important dates — Dryvn Life" },
      {
        name: "description",
        content: "Birthdays, anniversaries, and reminders — with a nudge for gift ideas.",
      },
    ],
  }),
  component: DatesPage,
});

const iconFor = (t: ImportantDate["type"]) =>
  t === "birthday" ? Cake : t === "anniversary" ? Heart : Bell;

const toneFor = (t: ImportantDate["type"]): "life" | "warm" | "amber" =>
  t === "birthday" ? "life" : t === "anniversary" ? "warm" : "amber";

function DatesPage() {
  const { data: importantDates, isError, error } = useImportantDates();
  const next = importantDates[0];

  // Search runs over the flat list and the months regroup from the matches,
  // so a query never leaves an empty month heading behind.
  const {
    query: dateQuery,
    setQuery: setDateQuery,
    results: dateMatches,
    active: dateSearchActive,
  } = useListFilter(importantDates, (d) => [d.title, d.who]);

  const byMonth = useMemo(() => {
    const groups = new Map<string, ImportantDate[]>();
    for (const d of dateMatches) {
      if (!groups.has(d.monthLabel)) groups.set(d.monthLabel, []);
      groups.get(d.monthLabel)!.push(d);
    }
    return Array.from(groups.entries());
  }, [dateMatches]);

  // `next` is undefined when the list is empty (live mode before any date
  // is added). Guard the icon and spotlight so the page renders a calm
  // empty state instead of throwing into the error boundary.
  const NextIcon = next ? iconFor(next.type) : Bell;

  return (
    <AppShell>
      <Link
        to="/more"
        className="mb-4 inline-flex items-center gap-1 text-sm"
        style={{ color: "var(--dryvn-primary)" }}
      >
        <ChevronLeft className="h-4 w-4" /> More
      </Link>

      <HeroCard
        eyebrow="Ahead"
        title={
          <>
            Important{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              dates
            </em>
            .
          </>
        }
      >
        <p className="text-sm" style={{ color: "var(--dryvn-primary-deep)" }}>
          Never miss the moments that matter. Dryvn nudges you a week ahead.
        </p>
      </HeroCard>

      {/* Next-up spotlight */}
      {next && (
        <DCard className="mt-6 overflow-hidden p-0">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div
                className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl"
                style={{ backgroundImage: "var(--grad-warm)" }}
              >
                <NextIcon className="h-6 w-6" style={{ color: "var(--dryvn-warm-deep)" }} />
              </div>
              <div>
                <p
                  className="font-mono text-[10px] uppercase tracking-[0.18em]"
                  style={{ color: "var(--dryvn-warm-deep)" }}
                >
                  Next up
                </p>
                <p
                  className="font-display text-2xl leading-tight"
                  style={{ color: "var(--dryvn-ink)" }}
                >
                  {next.title}
                </p>
                <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                  {next.dateLabel}
                  {next.who ? ` · ${next.who}` : ""}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p
                className="font-display text-5xl leading-none"
                style={{ color: "var(--dryvn-primary-deep)" }}
              >
                {next.daysUntil}
              </p>
              <p
                className="text-[10px] uppercase tracking-widest"
                style={{ color: "var(--dryvn-grey)" }}
              >
                days away
              </p>
            </div>
          </div>
          {next.giftIdea && (
            <div
              className="flex items-center gap-2 px-5 py-3 text-sm"
              style={{
                borderTop: "1px solid var(--dryvn-line)",
                backgroundColor: "color-mix(in oklab, var(--dryvn-life) 10%, transparent)",
              }}
            >
              <Gift className="h-4 w-4" style={{ color: "var(--dryvn-primary)" }} />
              <span style={{ color: "var(--dryvn-ink)" }}>
                Gift idea saved: <span className="italic">{next.giftIdea}</span>
              </span>
            </div>
          )}
        </DCard>
      )}

      {isError && (
        <div className="mt-6">
          <LoadError error={error} />
        </div>
      )}

      {!isError && importantDates.length === 0 && (
        <div className="mt-6">
          <EmptyState
            title="No dates saved yet. Text your Dryvn number to add a birthday or anniversary — it'll show up here with a nudge a week ahead."
            smsExamples={[
              "mom's birthday March 12",
              "add anniversary Aug 2 — Sarah & Mike, 14 years",
            ]}
          />
        </div>
      )}

      {/* Timeline by month */}
      {importantDates.length > 0 && (
        <div className="mt-8">
          <ListSearch
            value={dateQuery}
            onChange={setDateQuery}
            placeholder="Search dates…"
            label="Search important dates by name"
            className="max-w-sm"
          />
          {dateSearchActive && dateMatches.length === 0 && (
            <DCard>
              <p className="text-sm" style={{ color: "var(--dryvn-grey)" }}>
                No dates match that search.
              </p>
            </DCard>
          )}
        </div>
      )}

      <div className="mt-6 space-y-6">
        {byMonth.map(([month, list]) => (
          <section key={month}>
            <SectionHeader eyebrow="Month" title={month} />
            <CollapsibleList items={list} resource="dates" filtered={dateSearchActive}>
              {(shown) => (
                <DCard className="p-0">
                  <ul>
                    {shown.map((d, i) => {
                      const Icon = iconFor(d.type);
                      return (
                        <li
                          key={d.id}
                          className="flex items-center gap-3 p-4"
                          style={{
                            borderTop: i === 0 ? "none" : "1px solid var(--dryvn-line)",
                          }}
                        >
                          <span
                            className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
                            style={{
                              backgroundColor:
                                "color-mix(in oklab, var(--dryvn-warm) 22%, transparent)",
                            }}
                          >
                            <Icon className="h-4 w-4" style={{ color: "var(--dryvn-warm-deep)" }} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                                {d.title}
                              </p>
                              <Pill tone={toneFor(d.type)}>{d.type}</Pill>
                            </div>
                            <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                              {d.dateLabel}
                              {d.who ? ` · ${d.who}` : ""}
                            </p>
                            {d.giftIdea && (
                              <p
                                className="mt-1 inline-flex items-center gap-1 text-[11px] italic"
                                style={{ color: "var(--dryvn-primary-deep)" }}
                              >
                                <Gift className="h-3 w-3" /> {d.giftIdea}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-sm" style={{ color: "var(--dryvn-ink)" }}>
                              {d.daysUntil}d
                            </p>
                            <p
                              className="text-[10px] uppercase tracking-wider"
                              style={{ color: "var(--dryvn-grey)" }}
                            >
                              away
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </DCard>
              )}
            </CollapsibleList>
          </section>
        ))}
      </div>

      {/* SMS hint */}
      <DCard className="mt-6">
        <p className="flex items-center gap-2 text-sm" style={{ color: "var(--dryvn-ink)" }}>
          <MessageCircle className="h-4 w-4" style={{ color: "var(--dryvn-primary)" }} />
          Text Dryvn to add one:
        </p>
        <p
          className="mt-2 inline-block rounded-lg px-3 py-1.5 font-mono text-xs"
          style={{ backgroundColor: "var(--dryvn-page-tint)", color: "var(--dryvn-primary-deep)" }}
        >
          “add anniversary Aug 2 — Sarah &amp; Mike, 14 years”
        </p>
      </DCard>
    </AppShell>
  );
}
